import { createSignal, onMount, onCleanup, Show, createEffect } from 'solid-js';
import { useTheme } from '../theme/ThemeProvider';

// Turning sprites (up/down facing) - one randomly selected
const TURN_SPRITES = [
  '/sprites/u1.png',
  '/sprites/u2.png',
  '/sprites/u3.png',
  '/sprites/u4.png',
  '/sprites/d1.png',
  '/sprites/d2.png',
  '/sprites/d3.png',
  '/sprites/d4.png',
];

// Select one turning sprite randomly at module load (same for all turns)
const TURN_SPRITE = TURN_SPRITES[Math.floor(Math.random() * TURN_SPRITES.length)];

/**
 * RedSpriteWalker - 8-bit Pokemon trainer sprite walker
 * Realistic movement with random direction changes and turning sprites
 */
export function RedSpriteWalker() {
  const { preset } = useTheme();
  const [isVisible, setIsVisible] = createSignal(false);
  const [position, setPosition] = createSignal(0); // Current X position
  const [direction, setDirection] = createSignal<'left' | 'right'>('right');
  const [isMoving, setIsMoving] = createSignal(false);
  const [frame, setFrame] = createSignal(0); // Animation frame 0-3
  const [isTurning, setIsTurning] = createSignal(false);
  const [turningSprite, setTurningSprite] = createSignal('');

  // Constants
  const SPRITE_WIDTH = 52;
  const SPEED = 2; // Pixels per frame
  const FRAME_DURATION = 150; // ms per animation frame
  const TURN_DURATION = 400; // ms to show turning sprite

  // Refs for animation loop
  let animationId: number | null = null;
  let lastAnimFrameTime = Date.now();
  let turnPoints: number[] = [];
  let currentTurnIndex = 0;
  let turnTimeoutId: number | null = null;
  let journeyResetTimeoutId: number | null = null;

  const resetJourney = () => {
    const viewportWidth = window.innerWidth;
    const startFromLeft = Math.random() > 0.5;

    // Set new direction and starting position
    setDirection(startFromLeft ? 'right' : 'left');
    setPosition(startFromLeft ? -SPRITE_WIDTH : viewportWidth);

    // Reset frame
    setFrame(0);

    // Generate new turn points for this journey
    const numTurns = Math.floor(Math.random() * 4) + 1;
    turnPoints = [];
    for (let i = 0; i < numTurns; i++) {
      const point = Math.random() * (viewportWidth * 0.6) + viewportWidth * 0.2;
      turnPoints.push(point);
    }
    turnPoints.sort((a, b) => (startFromLeft ? a - b : b - a));
    currentTurnIndex = 0;
  };

  const animate = () => {
    if (!isMoving()) {
      return;
    }

    // Don't move while turning
    if (isTurning()) {
      animationId = requestAnimationFrame(animate);
      return;
    }

    const now = Date.now();
    const viewportWidth = window.innerWidth;

    // Update animation frame
    if (now - lastAnimFrameTime > FRAME_DURATION) {
      setFrame(f => (f + 1) % 4);
      lastAnimFrameTime = now;
    }

    // Update position
    setPosition(prev => {
      const currentDir = direction();
      const newPos = currentDir === 'right' ? prev + SPEED : prev - SPEED;

      // Check for turn points
      if (currentTurnIndex < turnPoints.length) {
        const turnPoint = turnPoints[currentTurnIndex];
        const shouldTurn = currentDir === 'right' ? newPos >= turnPoint : newPos <= turnPoint;

        if (shouldTurn) {
          currentTurnIndex++;
          setTurningSprite(TURN_SPRITE);
          setIsTurning(true);

          turnTimeoutId = window.setTimeout(() => {
            setDirection(d => (d === 'left' ? 'right' : 'left'));
            setIsTurning(false);
            turnTimeoutId = null;
          }, TURN_DURATION);
        }
      }

      // Check boundaries - reset journey
      if (newPos < -SPRITE_WIDTH || newPos > viewportWidth + SPRITE_WIDTH) {
        // Journey complete, start new one after brief delay
        journeyResetTimeoutId = window.setTimeout(() => {
          resetJourney();
          journeyResetTimeoutId = null;
        }, 500);
      }

      return newPos;
    });

    if (isMoving()) {
      animationId = requestAnimationFrame(animate);
    }
  };

  onMount(() => {
    // Initial setup
    const startRight = Math.random() > 0.5;
    setDirection(startRight ? 'right' : 'left');
    setPosition(startRight ? -SPRITE_WIDTH : window.innerWidth);
    setIsVisible(true);
    setIsMoving(true);

    // Generate initial turn points
    const viewportWidth = window.innerWidth;
    const numTurns = Math.floor(Math.random() * 4) + 1;
    for (let i = 0; i < numTurns; i++) {
      const point = Math.random() * (viewportWidth * 0.6) + viewportWidth * 0.2;
      turnPoints.push(point);
    }
    turnPoints.sort((a, b) => (startRight ? a - b : b - a));

    // Start animation loop
    lastAnimFrameTime = Date.now();
    animationId = requestAnimationFrame(animate);
  });

  onCleanup(() => {
    if (animationId !== null) {
      cancelAnimationFrame(animationId);
    }
    if (turnTimeoutId !== null) {
      clearTimeout(turnTimeoutId);
    }
    if (journeyResetTimeoutId !== null) {
      clearTimeout(journeyResetTimeoutId);
    }
  });

  // Handle window resize
  createEffect(() => {
    const handleResize = () => {
      const viewportWidth = window.innerWidth;
      setPosition(prev => {
        // Keep sprite on screen if resized
        if (prev < -SPRITE_WIDTH) {
          return -SPRITE_WIDTH;
        }
        if (prev > viewportWidth + SPRITE_WIDTH) {
          return viewportWidth + SPRITE_WIDTH;
        }
        return prev;
      });
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  return (
    <Show when={preset() === '8-bit' && isVisible()}>
      <div class="red-sprite-container" aria-hidden="true" role="presentation">
        <Show
          when={isTurning()}
          fallback={
            <div
              class={`red-sprite red-sprite--${direction()}`}
              style={{
                transform: `translateX(${position()}px)`,
                'background-position-x': `${-frame() * 52}px`,
              }}
            />
          }
        >
          {/* Turning sprite - randomly selected u/d sprite */}
          <div
            class="red-sprite red-sprite--turning"
            style={{
              transform: `translateX(${position()}px)`,
              'background-image': `url('${turningSprite()}')`,
            }}
          />
        </Show>
      </div>
    </Show>
  );
}
